import { useParams } from "react-router-dom";
import { HistoryIcon } from "lucide-react";
import { useMemo } from "react";

import { useNavigateWithSearch } from "@/hooks";
import { DatasetRead } from "@/types/datasets";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import UserAttribution from "@/components/UserAttribution";

interface VersionHistoryMenuProps {
  datasets: DatasetRead[];
  sortedDatasets: DatasetRead[];
  currentVersion?: number;
  isDraft: boolean;
}

export const getVersionTitle = (version_number?: number) =>
  !isNaN(version_number!) && version_number !== -1
    ? `Version #${version_number}`
    : (version_number && "Draft") || "Loading...";

const VersionHistoryMenu = ({
  sortedDatasets,
  currentVersion,
  isDraft,
}: VersionHistoryMenuProps) => {
  const { datasetGroupId, workspaceId, datasetId } = useParams();
  const navigateWithSearch = useNavigateWithSearch();

  const title = useMemo(() => getVersionTitle(currentVersion), [currentVersion]);

  const getVersion = () => {
    if (isDraft) {
      return "Draft";
    }

    if (!isDraft && currentVersion) {
      return title;
    }

    return `Version History`;
  };

  return (
    <div className="flex h-8 min-w-[156px]">
      <DropdownMenu>
        <DropdownMenuTrigger disabled={false} className="flex w-full items-center">
          <div className="flex items-center justify-start">
            <HistoryIcon size={15} className="mr-1" /> {getVersion()}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="!max-h-[300px] overflow-y-auto">
          {sortedDatasets.map(({ id, version_number, author }) => (
            <DropdownMenuItem
              onClick={() =>
                navigateWithSearch(
                  `/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${id}`,
                )
              }
              className={`${
                String(id) === datasetId && "border border-blue-200 bg-blue-50"
              } flex flex-col items-start whitespace-nowrap`}
              key={id}
            >
              <span>{getVersionTitle(version_number)}</span>
              {author && <UserAttribution email={author} className="mt-1" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default VersionHistoryMenu;
